import { supabase } from './supabase';

interface JobSeed {
  title: string;
  description: string;
  company: string;
  location: string;
  remote: boolean;
  salary_min: number | null;
  salary_max: number | null;
  experience_level: 'entry' | 'mid' | 'senior' | 'executive';
  job_type: 'full_time' | 'part_time' | 'contract' | 'internship';
  skills: string[];
  industry: string;
}

const jobs: JobSeed[] = [
  // Technology (15 jobs)
  {
    title: 'Senior Full Stack Developer',
    description: 'Build and maintain scalable web applications using modern technologies. Work with React, Node.js, and cloud infrastructure.',
    company: 'TechFlow Solutions',
    location: 'San Francisco, CA',
    remote: true,
    salary_min: 130000,
    salary_max: 180000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['React', 'Node.js', 'TypeScript', 'AWS', 'PostgreSQL'],
    industry: 'Technology',
  },
  {
    title: 'Frontend Developer',
    description: 'Create beautiful, responsive user interfaces with React and modern CSS frameworks.',
    company: 'DesignTech Inc',
    location: 'Austin, TX',
    remote: true,
    salary_min: 85000,
    salary_max: 120000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['React', 'JavaScript', 'CSS', 'Tailwind', 'Figma'],
    industry: 'Technology',
  },
  {
    title: 'Junior Software Engineer',
    description: 'Join our engineering team to learn and grow your coding skills. Work on real-world projects with mentorship.',
    company: 'StartupHub',
    location: 'Remote',
    remote: true,
    salary_min: 65000,
    salary_max: 85000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Python', 'Git', 'SQL', 'APIs', 'Testing'],
    industry: 'Technology',
  },
  {
    title: 'DevOps Engineer',
    description: 'Manage CI/CD pipelines, infrastructure automation, and cloud deployments.',
    company: 'CloudScale Systems',
    location: 'Seattle, WA',
    remote: false,
    salary_min: 115000,
    salary_max: 155000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Docker', 'Kubernetes', 'AWS', 'Terraform', 'CI/CD'],
    industry: 'Technology',
  },
  {
    title: 'Mobile Developer (iOS)',
    description: 'Build native iOS applications using Swift and modern Apple frameworks.',
    company: 'AppVentures',
    location: 'New York, NY',
    remote: false,
    salary_min: 105000,
    salary_max: 145000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Swift', 'iOS', 'SwiftUI', 'Xcode', 'REST APIs'],
    industry: 'Technology',
  },
  {
    title: 'Data Engineer',
    description: 'Design and maintain data pipelines, warehouses, and analytics infrastructure.',
    company: 'DataWorks Corp',
    location: 'Chicago, IL',
    remote: true,
    salary_min: 120000,
    salary_max: 165000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Python', 'SQL', 'Spark', 'Airflow', 'BigQuery'],
    industry: 'Technology',
  },
  {
    title: 'QA Automation Engineer',
    description: 'Develop and maintain automated test suites for web and mobile applications.',
    company: 'QualityFirst Tech',
    location: 'Boston, MA',
    remote: true,
    salary_min: 90000,
    salary_max: 125000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Selenium', 'Cypress', 'Jest', 'Python', 'CI/CD'],
    industry: 'Technology',
  },
  {
    title: 'Product Manager',
    description: 'Lead product strategy and roadmap for consumer-facing applications.',
    company: 'InnovateTech',
    location: 'San Diego, CA',
    remote: false,
    salary_min: 125000,
    salary_max: 170000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Product Strategy', 'Agile', 'Analytics', 'User Research', 'Roadmapping'],
    industry: 'Technology',
  },
  {
    title: 'UX Designer',
    description: 'Design intuitive user experiences and create prototypes for digital products.',
    company: 'DesignStudio Pro',
    location: 'Portland, OR',
    remote: true,
    salary_min: 95000,
    salary_max: 130000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Figma', 'User Research', 'Prototyping', 'Wireframing', 'Design Systems'],
    industry: 'Technology',
  },
  {
    title: 'Cybersecurity Analyst',
    description: 'Monitor and protect systems from security threats. Implement security best practices.',
    company: 'SecureNet Solutions',
    location: 'Washington, DC',
    remote: false,
    salary_min: 110000,
    salary_max: 150000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Security', 'Penetration Testing', 'SIEM', 'Compliance', 'Incident Response'],
    industry: 'Technology',
  },
  {
    title: 'Machine Learning Engineer',
    description: 'Develop and deploy ML models for production systems. Work with large datasets.',
    company: 'AI Innovations',
    location: 'Palo Alto, CA',
    remote: true,
    salary_min: 145000,
    salary_max: 200000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Python', 'TensorFlow', 'PyTorch', 'ML Ops', 'Deep Learning'],
    industry: 'Technology',
  },
  {
    title: 'Technical Writer',
    description: 'Create clear technical documentation for developers and end users.',
    company: 'DocuTech',
    location: 'Remote',
    remote: true,
    salary_min: 75000,
    salary_max: 100000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Technical Writing', 'Documentation', 'APIs', 'Markdown', 'Git'],
    industry: 'Technology',
  },
  {
    title: 'IT Support Specialist',
    description: 'Provide technical support to employees. Troubleshoot hardware and software issues.',
    company: 'TechSupport Plus',
    location: 'Denver, CO',
    remote: false,
    salary_min: 55000,
    salary_max: 75000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Windows', 'macOS', 'Networking', 'Troubleshooting', 'Customer Service'],
    industry: 'Technology',
  },
  {
    title: 'Solutions Architect',
    description: 'Design technical solutions for enterprise clients. Lead implementation projects.',
    company: 'Enterprise Solutions Group',
    location: 'Atlanta, GA',
    remote: false,
    salary_min: 140000,
    salary_max: 185000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Architecture', 'AWS', 'System Design', 'Client Relations', 'Technical Sales'],
    industry: 'Technology',
  },
  {
    title: 'Software Engineering Intern',
    description: 'Summer internship program for computer science students. Hands-on coding experience.',
    company: 'Tech Giants Inc',
    location: 'Mountain View, CA',
    remote: false,
    salary_min: 30,
    salary_max: 45,
    experience_level: 'entry',
    job_type: 'internship',
    skills: ['Programming', 'Git', 'Algorithms', 'Problem Solving', 'Teamwork'],
    industry: 'Technology',
  },

  // Healthcare (12 jobs)
  {
    title: 'Registered Nurse - ICU',
    description: 'Provide critical care to patients in intensive care unit. Work in fast-paced environment.',
    company: 'Memorial Hospital',
    location: 'Detroit, MI',
    remote: false,
    salary_min: 70000,
    salary_max: 95000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Critical Care', 'Patient Care', 'Medical Equipment', 'Team Collaboration', 'Emergency Response'],
    industry: 'Healthcare',
  },
  {
    title: 'Physical Therapist',
    description: 'Help patients recover mobility and manage pain through therapeutic exercises.',
    company: 'Rehab Center of Excellence',
    location: 'Phoenix, AZ',
    remote: false,
    salary_min: 75000,
    salary_max: 95000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Physical Therapy', 'Patient Assessment', 'Treatment Planning', 'Communication', 'Rehabilitation'],
    industry: 'Healthcare',
  },
  {
    title: 'Medical Assistant',
    description: 'Support physicians with patient care and administrative tasks in busy clinic.',
    company: 'Family Health Clinic',
    location: 'Houston, TX',
    remote: false,
    salary_min: 35000,
    salary_max: 45000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Patient Care', 'Medical Records', 'Vital Signs', 'Scheduling', 'HIPAA Compliance'],
    industry: 'Healthcare',
  },
  {
    title: 'Pharmacist',
    description: 'Dispense medications and provide pharmaceutical care to patients.',
    company: 'Community Pharmacy Network',
    location: 'Dallas, TX',
    remote: false,
    salary_min: 110000,
    salary_max: 135000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Pharmacy', 'Medication Management', 'Patient Counseling', 'Drug Interactions', 'Regulations'],
    industry: 'Healthcare',
  },
  {
    title: 'Healthcare Data Analyst',
    description: 'Analyze healthcare data to improve patient outcomes and operational efficiency.',
    company: 'HealthMetrics Inc',
    location: 'Remote',
    remote: true,
    salary_min: 80000,
    salary_max: 110000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Data Analysis', 'SQL', 'Healthcare Analytics', 'Excel', 'Visualization'],
    industry: 'Healthcare',
  },
  {
    title: 'Medical Coder',
    description: 'Assign diagnosis and procedure codes for medical billing and insurance.',
    company: 'Medical Coding Services',
    location: 'Remote',
    remote: true,
    salary_min: 50000,
    salary_max: 65000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['ICD-10', 'CPT Coding', 'Medical Terminology', 'Attention to Detail', 'Healthcare Billing'],
    industry: 'Healthcare',
  },
  {
    title: 'Clinical Research Coordinator',
    description: 'Manage clinical trials and ensure compliance with research protocols.',
    company: 'Research Medical Center',
    location: 'Baltimore, MD',
    remote: false,
    salary_min: 60000,
    salary_max: 80000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Clinical Research', 'GCP', 'Patient Recruitment', 'Data Collection', 'Regulatory Compliance'],
    industry: 'Healthcare',
  },
  {
    title: 'Radiology Technician',
    description: 'Operate imaging equipment and assist radiologists with diagnostic procedures.',
    company: 'Imaging Center',
    location: 'Miami, FL',
    remote: false,
    salary_min: 55000,
    salary_max: 72000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Radiology', 'X-Ray', 'CT Scan', 'Patient Safety', 'Medical Imaging'],
    industry: 'Healthcare',
  },
  {
    title: 'Healthcare Administrator',
    description: 'Oversee daily operations of medical facility and manage staff.',
    company: 'Wellness Medical Group',
    location: 'Philadelphia, PA',
    remote: false,
    salary_min: 85000,
    salary_max: 115000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Healthcare Management', 'Operations', 'Budgeting', 'Staff Management', 'Compliance'],
    industry: 'Healthcare',
  },
  {
    title: 'Mental Health Counselor',
    description: 'Provide therapy and counseling to individuals and groups.',
    company: 'Mindful Counseling Center',
    location: 'Seattle, WA',
    remote: true,
    salary_min: 55000,
    salary_max: 75000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Counseling', 'Therapy', 'Mental Health', 'Crisis Intervention', 'Empathy'],
    industry: 'Healthcare',
  },
  {
    title: 'Dental Hygienist',
    description: 'Provide preventive dental care and educate patients on oral health.',
    company: 'Bright Smile Dentistry',
    location: 'San Antonio, TX',
    remote: false,
    salary_min: 65000,
    salary_max: 85000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Dental Hygiene', 'Patient Care', 'Oral Health Education', 'X-Rays', 'Periodontal Care'],
    industry: 'Healthcare',
  },
  {
    title: 'Medical Laboratory Technician',
    description: 'Perform laboratory tests and analyze samples for disease diagnosis.',
    company: 'Diagnostic Labs',
    location: 'Minneapolis, MN',
    remote: false,
    salary_min: 52000,
    salary_max: 68000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Laboratory Testing', 'Microscopy', 'Quality Control', 'Medical Technology', 'Data Entry'],
    industry: 'Healthcare',
  },

  // Finance (10 jobs)
  {
    title: 'Financial Analyst',
    description: 'Analyze financial data and create reports to support business decisions.',
    company: 'Capital Investments',
    location: 'New York, NY',
    remote: false,
    salary_min: 75000,
    salary_max: 100000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Financial Analysis', 'Excel', 'Financial Modeling', 'Forecasting', 'Reporting'],
    industry: 'Finance',
  },
  {
    title: 'Investment Banker',
    description: 'Advise clients on mergers, acquisitions, and capital raising strategies.',
    company: 'Global Finance Group',
    location: 'New York, NY',
    remote: false,
    salary_min: 120000,
    salary_max: 180000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Investment Banking', 'M&A', 'Financial Modeling', 'Valuation', 'Client Relations'],
    industry: 'Finance',
  },
  {
    title: 'Accountant',
    description: 'Manage financial records, prepare tax returns, and ensure compliance.',
    company: 'Accounting Associates',
    location: 'Charlotte, NC',
    remote: true,
    salary_min: 60000,
    salary_max: 80000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Accounting', 'QuickBooks', 'Tax Preparation', 'Financial Statements', 'GAAP'],
    industry: 'Finance',
  },
  {
    title: 'Risk Analyst',
    description: 'Assess and mitigate financial risks for investment portfolios.',
    company: 'Risk Management Corp',
    location: 'Chicago, IL',
    remote: false,
    salary_min: 85000,
    salary_max: 115000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Risk Management', 'Data Analysis', 'Financial Modeling', 'Statistics', 'Regulations'],
    industry: 'Finance',
  },
  {
    title: 'Financial Advisor',
    description: 'Provide personalized financial planning and investment advice to clients.',
    company: 'Wealth Management Partners',
    location: 'San Francisco, CA',
    remote: false,
    salary_min: 70000,
    salary_max: 120000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Financial Planning', 'Investment Strategy', 'Client Relations', 'CFP', 'Portfolio Management'],
    industry: 'Finance',
  },
  {
    title: 'Credit Analyst',
    description: 'Evaluate creditworthiness of loan applicants and assess financial risk.',
    company: 'Regional Bank',
    location: 'Detroit, MI',
    remote: false,
    salary_min: 58000,
    salary_max: 75000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Credit Analysis', 'Financial Statements', 'Risk Assessment', 'Banking', 'Due Diligence'],
    industry: 'Finance',
  },
  {
    title: 'Controller',
    description: 'Oversee accounting operations and financial reporting for growing company.',
    company: 'Manufacturing Solutions Inc',
    location: 'Cleveland, OH',
    remote: false,
    salary_min: 110000,
    salary_max: 145000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Accounting', 'Financial Reporting', 'Management', 'ERP Systems', 'Compliance'],
    industry: 'Finance',
  },
  {
    title: 'Payroll Specialist',
    description: 'Process payroll and manage employee compensation and benefits.',
    company: 'HR Solutions Group',
    location: 'Remote',
    remote: true,
    salary_min: 48000,
    salary_max: 62000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Payroll', 'ADP', 'Benefits Administration', 'Tax Compliance', 'Data Entry'],
    industry: 'Finance',
  },
  {
    title: 'Compliance Officer',
    description: 'Ensure company adheres to financial regulations and internal policies.',
    company: 'Regulatory Compliance Firm',
    location: 'Boston, MA',
    remote: false,
    salary_min: 95000,
    salary_max: 125000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Compliance', 'Regulations', 'Auditing', 'Risk Management', 'Policy Development'],
    industry: 'Finance',
  },
  {
    title: 'Junior Financial Analyst',
    description: 'Support financial analysis and reporting under guidance of senior analysts.',
    company: 'Financial Services Corp',
    location: 'Remote',
    remote: true,
    salary_min: 55000,
    salary_max: 70000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Excel', 'Financial Analysis', 'Data Analysis', 'Reporting', 'Communication'],
    industry: 'Finance',
  },

  // Education (10 jobs)
  {
    title: 'High School Math Teacher',
    description: 'Teach mathematics to high school students. Create engaging lesson plans.',
    company: 'Lincoln High School',
    location: 'Portland, OR',
    remote: false,
    salary_min: 50000,
    salary_max: 70000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Teaching', 'Mathematics', 'Classroom Management', 'Curriculum Development', 'Student Assessment'],
    industry: 'Education',
  },
  {
    title: 'Elementary School Teacher',
    description: 'Inspire young minds and create engaging learning experiences for children.',
    company: 'Sunshine Elementary',
    location: 'Austin, TX',
    remote: false,
    salary_min: 45000,
    salary_max: 62000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Elementary Education', 'Classroom Management', 'Lesson Planning', 'Child Development', 'Communication'],
    industry: 'Education',
  },
  {
    title: 'Special Education Teacher',
    description: 'Work with students with special needs. Develop individualized education programs.',
    company: 'Gateway School District',
    location: 'Denver, CO',
    remote: false,
    salary_min: 52000,
    salary_max: 72000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Special Education', 'IEP Development', 'Behavior Management', 'Patience', 'Adaptability'],
    industry: 'Education',
  },
  {
    title: 'College Professor - Computer Science',
    description: 'Teach undergraduate courses and conduct research in computer science.',
    company: 'State University',
    location: 'Ann Arbor, MI',
    remote: false,
    salary_min: 80000,
    salary_max: 120000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Computer Science', 'Research', 'Teaching', 'Academic Writing', 'Mentoring'],
    industry: 'Education',
  },
  {
    title: 'School Counselor',
    description: 'Provide academic and emotional support to students. Guide college planning.',
    company: 'Metro High School',
    location: 'Chicago, IL',
    remote: false,
    salary_min: 55000,
    salary_max: 75000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Counseling', 'Student Support', 'Career Guidance', 'Crisis Intervention', 'College Planning'],
    industry: 'Education',
  },
  {
    title: 'Instructional Designer',
    description: 'Design engaging online courses and learning materials for digital education.',
    company: 'EdTech Innovations',
    location: 'Remote',
    remote: true,
    salary_min: 65000,
    salary_max: 90000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Instructional Design', 'E-Learning', 'LMS', 'Curriculum Development', 'Multimedia'],
    industry: 'Education',
  },
  {
    title: 'Academic Advisor',
    description: 'Guide students through degree planning and academic success strategies.',
    company: 'Community College',
    location: 'Phoenix, AZ',
    remote: false,
    salary_min: 45000,
    salary_max: 60000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Advising', 'Student Services', 'Communication', 'Organization', 'Higher Education'],
    industry: 'Education',
  },
  {
    title: 'ESL Teacher',
    description: 'Teach English to non-native speakers. Create inclusive learning environment.',
    company: 'Language Learning Center',
    location: 'San Diego, CA',
    remote: false,
    salary_min: 42000,
    salary_max: 58000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['ESL Teaching', 'Language Instruction', 'Cultural Sensitivity', 'Lesson Planning', 'TESOL'],
    industry: 'Education',
  },
  {
    title: 'Curriculum Developer',
    description: 'Design K-12 curriculum materials aligned with educational standards.',
    company: 'Curriculum Solutions',
    location: 'Remote',
    remote: true,
    salary_min: 60000,
    salary_max: 85000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Curriculum Design', 'Education Standards', 'Content Development', 'Teaching', 'Assessment'],
    industry: 'Education',
  },
  {
    title: 'Education Administrator',
    description: 'Oversee school operations and coordinate educational programs.',
    company: 'Jefferson School District',
    location: 'Seattle, WA',
    remote: false,
    salary_min: 75000,
    salary_max: 100000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Educational Leadership', 'Operations Management', 'Policy Development', 'Budgeting', 'Staff Management'],
    industry: 'Education',
  },

  // Retail (8 jobs)
  {
    title: 'Store Manager',
    description: 'Manage daily operations of retail store. Lead team and drive sales.',
    company: 'Fashion Retail Co',
    location: 'Los Angeles, CA',
    remote: false,
    salary_min: 55000,
    salary_max: 75000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Retail Management', 'Sales', 'Team Leadership', 'Inventory Management', 'Customer Service'],
    industry: 'Retail',
  },
  {
    title: 'Visual Merchandiser',
    description: 'Create appealing product displays and maintain store aesthetics.',
    company: 'Home Goods Plus',
    location: 'Dallas, TX',
    remote: false,
    salary_min: 42000,
    salary_max: 58000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Visual Merchandising', 'Creativity', 'Display Design', 'Retail', 'Trend Analysis'],
    industry: 'Retail',
  },
  {
    title: 'Sales Associate',
    description: 'Assist customers with purchases and provide excellent service.',
    company: 'Outdoor Adventure Gear',
    location: 'Denver, CO',
    remote: false,
    salary_min: 30000,
    salary_max: 40000,
    experience_level: 'entry',
    job_type: 'part_time',
    skills: ['Customer Service', 'Sales', 'Product Knowledge', 'Communication', 'Cash Handling'],
    industry: 'Retail',
  },
  {
    title: 'Buyer',
    description: 'Select and purchase merchandise for retail stores based on trends and data.',
    company: 'Department Store Chain',
    location: 'New York, NY',
    remote: false,
    salary_min: 65000,
    salary_max: 90000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Buying', 'Negotiation', 'Trend Analysis', 'Vendor Relations', 'Merchandising'],
    industry: 'Retail',
  },
  {
    title: 'E-commerce Manager',
    description: 'Oversee online retail operations and digital marketing strategies.',
    company: 'Online Retail Hub',
    location: 'Remote',
    remote: true,
    salary_min: 70000,
    salary_max: 95000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['E-commerce', 'Digital Marketing', 'Analytics', 'SEO', 'Inventory Management'],
    industry: 'Retail',
  },
  {
    title: 'Loss Prevention Specialist',
    description: 'Prevent theft and ensure store security. Monitor surveillance systems.',
    company: 'Retail Security Services',
    location: 'Miami, FL',
    remote: false,
    salary_min: 40000,
    salary_max: 55000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Security', 'Loss Prevention', 'Surveillance', 'Investigation', 'Report Writing'],
    industry: 'Retail',
  },
  {
    title: 'Inventory Coordinator',
    description: 'Manage stock levels and coordinate with suppliers for timely deliveries.',
    company: 'Supply Chain Retail',
    location: 'Atlanta, GA',
    remote: false,
    salary_min: 45000,
    salary_max: 60000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Inventory Management', 'Supply Chain', 'Excel', 'Organization', 'Logistics'],
    industry: 'Retail',
  },
  {
    title: 'Regional Sales Manager',
    description: 'Oversee multiple retail locations and drive regional sales performance.',
    company: 'National Retail Brand',
    location: 'Houston, TX',
    remote: false,
    salary_min: 85000,
    salary_max: 120000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Sales Management', 'Leadership', 'Multi-Unit Management', 'P&L', 'Strategy'],
    industry: 'Retail',
  },

  // Manufacturing (8 jobs)
  {
    title: 'Production Supervisor',
    description: 'Oversee manufacturing floor operations and ensure production targets are met.',
    company: 'Advanced Manufacturing Inc',
    location: 'Detroit, MI',
    remote: false,
    salary_min: 65000,
    salary_max: 85000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Manufacturing', 'Team Leadership', 'Quality Control', 'Safety', 'Production Planning'],
    industry: 'Manufacturing',
  },
  {
    title: 'Quality Control Inspector',
    description: 'Inspect products and ensure they meet quality standards and specifications.',
    company: 'Precision Parts Manufacturing',
    location: 'Cleveland, OH',
    remote: false,
    salary_min: 45000,
    salary_max: 60000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Quality Control', 'Inspection', 'Measurement Tools', 'Attention to Detail', 'ISO Standards'],
    industry: 'Manufacturing',
  },
  {
    title: 'Mechanical Engineer',
    description: 'Design and improve manufacturing processes and equipment.',
    company: 'Engineering Solutions Co',
    location: 'Indianapolis, IN',
    remote: false,
    salary_min: 75000,
    salary_max: 105000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Mechanical Engineering', 'CAD', 'Manufacturing', 'Problem Solving', 'Project Management'],
    industry: 'Manufacturing',
  },
  {
    title: 'Maintenance Technician',
    description: 'Perform preventive maintenance and repairs on manufacturing equipment.',
    company: 'Industrial Equipment Corp',
    location: 'Milwaukee, WI',
    remote: false,
    salary_min: 50000,
    salary_max: 68000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Maintenance', 'Troubleshooting', 'Electrical', 'Mechanical Systems', 'Safety'],
    industry: 'Manufacturing',
  },
  {
    title: 'Supply Chain Coordinator',
    description: 'Coordinate materials procurement and inventory for manufacturing operations.',
    company: 'Global Manufacturing Group',
    location: 'Charlotte, NC',
    remote: false,
    salary_min: 55000,
    salary_max: 72000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Supply Chain', 'Procurement', 'Inventory Management', 'ERP Systems', 'Logistics'],
    industry: 'Manufacturing',
  },
  {
    title: 'Plant Manager',
    description: 'Lead manufacturing facility operations and manage production teams.',
    company: 'Automotive Parts Manufacturing',
    location: 'Lansing, MI',
    remote: false,
    salary_min: 100000,
    salary_max: 135000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Manufacturing Management', 'Lean Manufacturing', 'Leadership', 'P&L Management', 'Continuous Improvement'],
    industry: 'Manufacturing',
  },
  {
    title: 'CNC Operator',
    description: 'Operate CNC machinery to produce precision parts according to specifications.',
    company: 'Precision Machining',
    location: 'Grand Rapids, MI',
    remote: false,
    salary_min: 42000,
    salary_max: 58000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['CNC Operation', 'Machining', 'Blueprint Reading', 'Quality Control', 'Safety'],
    industry: 'Manufacturing',
  },
  {
    title: 'Industrial Safety Manager',
    description: 'Develop and implement safety programs for manufacturing environment.',
    company: 'Safety First Manufacturing',
    location: 'Pittsburgh, PA',
    remote: false,
    salary_min: 80000,
    salary_max: 105000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Safety Management', 'OSHA', 'Risk Assessment', 'Training', 'Compliance'],
    industry: 'Manufacturing',
  },

  // Hospitality (8 jobs)
  {
    title: 'Hotel Front Desk Manager',
    description: 'Manage front desk operations and ensure excellent guest experiences.',
    company: 'Luxury Hotels Group',
    location: 'Las Vegas, NV',
    remote: false,
    salary_min: 45000,
    salary_max: 60000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Hotel Management', 'Customer Service', 'Team Leadership', 'PMS Systems', 'Problem Solving'],
    industry: 'Hospitality',
  },
  {
    title: 'Executive Chef',
    description: 'Lead kitchen operations and create innovative menus for upscale restaurant.',
    company: 'Fine Dining Restaurant',
    location: 'New Orleans, LA',
    remote: false,
    salary_min: 70000,
    salary_max: 95000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Culinary Arts', 'Menu Development', 'Kitchen Management', 'Food Safety', 'Leadership'],
    industry: 'Hospitality',
  },
  {
    title: 'Event Coordinator',
    description: 'Plan and execute events including weddings, conferences, and parties.',
    company: 'Event Planning Services',
    location: 'San Diego, CA',
    remote: false,
    salary_min: 42000,
    salary_max: 58000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Event Planning', 'Organization', 'Vendor Management', 'Communication', 'Budget Management'],
    industry: 'Hospitality',
  },
  {
    title: 'Restaurant Server',
    description: 'Provide exceptional dining service to guests in upscale restaurant.',
    company: 'Upscale Bistro',
    location: 'Nashville, TN',
    remote: false,
    salary_min: 25000,
    salary_max: 40000,
    experience_level: 'entry',
    job_type: 'part_time',
    skills: ['Customer Service', 'Food Service', 'Communication', 'Multitasking', 'POS Systems'],
    industry: 'Hospitality',
  },
  {
    title: 'Catering Manager',
    description: 'Manage catering operations for events and corporate functions.',
    company: 'Premier Catering Co',
    location: 'Washington, DC',
    remote: false,
    salary_min: 50000,
    salary_max: 70000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Catering', 'Event Management', 'Food Service', 'Sales', 'Team Leadership'],
    industry: 'Hospitality',
  },
  {
    title: 'Housekeeping Supervisor',
    description: 'Oversee housekeeping team and maintain hotel cleanliness standards.',
    company: 'Resort Hotels',
    location: 'Orlando, FL',
    remote: false,
    salary_min: 38000,
    salary_max: 52000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Housekeeping', 'Team Supervision', 'Quality Control', 'Scheduling', 'Training'],
    industry: 'Hospitality',
  },
  {
    title: 'Bartender',
    description: 'Craft cocktails and provide friendly service at upscale bar.',
    company: 'Cocktail Lounge',
    location: 'Austin, TX',
    remote: false,
    salary_min: 28000,
    salary_max: 45000,
    experience_level: 'entry',
    job_type: 'part_time',
    skills: ['Bartending', 'Mixology', 'Customer Service', 'Cash Handling', 'Beverage Knowledge'],
    industry: 'Hospitality',
  },
  {
    title: 'Hotel General Manager',
    description: 'Lead all hotel operations and drive property performance.',
    company: 'Boutique Hotel Chain',
    location: 'Savannah, GA',
    remote: false,
    salary_min: 85000,
    salary_max: 120000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Hotel Management', 'Operations', 'P&L Management', 'Leadership', 'Guest Relations'],
    industry: 'Hospitality',
  },

  // Marketing (9 jobs)
  {
    title: 'Digital Marketing Manager',
    description: 'Lead digital marketing campaigns across multiple channels and platforms.',
    company: 'Growth Marketing Agency',
    location: 'Los Angeles, CA',
    remote: true,
    salary_min: 75000,
    salary_max: 105000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Digital Marketing', 'SEO', 'SEM', 'Social Media', 'Analytics'],
    industry: 'Marketing',
  },
  {
    title: 'Content Marketing Specialist',
    description: 'Create compelling content to engage audiences and drive conversions.',
    company: 'Content Studio',
    location: 'Remote',
    remote: true,
    salary_min: 60000,
    salary_max: 80000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Content Marketing', 'Writing', 'SEO', 'Content Strategy', 'Social Media'],
    industry: 'Marketing',
  },
  {
    title: 'Social Media Manager',
    description: 'Manage social media presence and create engaging content for brand.',
    company: 'Brand Influence',
    location: 'Miami, FL',
    remote: true,
    salary_min: 55000,
    salary_max: 75000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Social Media Marketing', 'Content Creation', 'Community Management', 'Analytics', 'Copywriting'],
    industry: 'Marketing',
  },
  {
    title: 'Marketing Coordinator',
    description: 'Support marketing team with campaign execution and administrative tasks.',
    company: 'Marketing Solutions Inc',
    location: 'Chicago, IL',
    remote: false,
    salary_min: 42000,
    salary_max: 55000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Marketing', 'Project Coordination', 'Communication', 'Organization', 'Microsoft Office'],
    industry: 'Marketing',
  },
  {
    title: 'Brand Manager',
    description: 'Develop and execute brand strategy to strengthen market position.',
    company: 'Consumer Brands Co',
    location: 'New York, NY',
    remote: false,
    salary_min: 85000,
    salary_max: 115000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Brand Management', 'Strategy', 'Market Research', 'Product Marketing', 'Analytics'],
    industry: 'Marketing',
  },
  {
    title: 'Email Marketing Specialist',
    description: 'Design and execute email campaigns to nurture leads and drive sales.',
    company: 'E-commerce Marketing',
    location: 'Remote',
    remote: true,
    salary_min: 52000,
    salary_max: 70000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Email Marketing', 'Mailchimp', 'Copywriting', 'A/B Testing', 'Analytics'],
    industry: 'Marketing',
  },
  {
    title: 'Marketing Analyst',
    description: 'Analyze marketing data and provide insights to optimize campaigns.',
    company: 'Data-Driven Marketing',
    location: 'San Francisco, CA',
    remote: true,
    salary_min: 65000,
    salary_max: 90000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Marketing Analytics', 'Data Analysis', 'SQL', 'Google Analytics', 'Reporting'],
    industry: 'Marketing',
  },
  {
    title: 'SEO Specialist',
    description: 'Improve organic search rankings and drive website traffic.',
    company: 'Digital Growth Agency',
    location: 'Remote',
    remote: true,
    salary_min: 58000,
    salary_max: 78000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['SEO', 'Google Search Console', 'Keyword Research', 'Link Building', 'Content Optimization'],
    industry: 'Marketing',
  },
  {
    title: 'Marketing Director',
    description: 'Lead marketing department and develop comprehensive marketing strategies.',
    company: 'Enterprise Solutions',
    location: 'Boston, MA',
    remote: false,
    salary_min: 120000,
    salary_max: 165000,
    experience_level: 'executive',
    job_type: 'full_time',
    skills: ['Marketing Strategy', 'Leadership', 'Budget Management', 'Team Management', 'Analytics'],
    industry: 'Marketing',
  },

  // Sales (10 jobs)
  {
    title: 'Account Executive',
    description: 'Build relationships with clients and close sales for B2B software solutions.',
    company: 'Software Sales Corp',
    location: 'Atlanta, GA',
    remote: true,
    salary_min: 65000,
    salary_max: 110000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Sales', 'B2B', 'CRM', 'Negotiation', 'Relationship Building'],
    industry: 'Sales',
  },
  {
    title: 'Sales Development Representative',
    description: 'Generate leads and qualify prospects for sales team.',
    company: 'Tech Sales Inc',
    location: 'Remote',
    remote: true,
    salary_min: 45000,
    salary_max: 65000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Lead Generation', 'Cold Calling', 'Sales', 'CRM', 'Communication'],
    industry: 'Sales',
  },
  {
    title: 'Sales Manager',
    description: 'Lead sales team and drive revenue growth through strategic initiatives.',
    company: 'Enterprise Sales Group',
    location: 'San Francisco, CA',
    remote: false,
    salary_min: 95000,
    salary_max: 135000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Sales Management', 'Leadership', 'Team Development', 'Strategy', 'Pipeline Management'],
    industry: 'Sales',
  },
  {
    title: 'Inside Sales Representative',
    description: 'Sell products and services via phone and email to small businesses.',
    company: 'Business Solutions Co',
    location: 'Phoenix, AZ',
    remote: true,
    salary_min: 40000,
    salary_max: 60000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Inside Sales', 'Phone Sales', 'CRM', 'Product Knowledge', 'Communication'],
    industry: 'Sales',
  },
  {
    title: 'Regional Sales Director',
    description: 'Oversee sales operations for entire region and manage sales managers.',
    company: 'National Sales Organization',
    location: 'Dallas, TX',
    remote: false,
    salary_min: 130000,
    salary_max: 180000,
    experience_level: 'executive',
    job_type: 'full_time',
    skills: ['Sales Leadership', 'Strategic Planning', 'P&L Management', 'Team Building', 'Analytics'],
    industry: 'Sales',
  },
  {
    title: 'Customer Success Manager',
    description: 'Ensure client satisfaction and drive account growth and retention.',
    company: 'SaaS Company',
    location: 'Remote',
    remote: true,
    salary_min: 70000,
    salary_max: 95000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Customer Success', 'Account Management', 'Communication', 'Problem Solving', 'SaaS'],
    industry: 'Sales',
  },
  {
    title: 'Sales Engineer',
    description: 'Provide technical expertise during sales process for complex solutions.',
    company: 'Enterprise Tech Solutions',
    location: 'Seattle, WA',
    remote: false,
    salary_min: 95000,
    salary_max: 130000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Sales Engineering', 'Technical Presentations', 'Solution Design', 'Sales', 'Cloud Technology'],
    industry: 'Sales',
  },
  {
    title: 'Business Development Manager',
    description: 'Identify new business opportunities and develop strategic partnerships.',
    company: 'Growth Partners Inc',
    location: 'New York, NY',
    remote: false,
    salary_min: 80000,
    salary_max: 115000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Business Development', 'Partnership Development', 'Negotiation', 'Strategy', 'Networking'],
    industry: 'Sales',
  },
  {
    title: 'Territory Sales Representative',
    description: 'Build and manage customer relationships within assigned territory.',
    company: 'Medical Device Sales',
    location: 'Denver, CO',
    remote: false,
    salary_min: 70000,
    salary_max: 105000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Territory Management', 'Sales', 'Relationship Building', 'Product Demonstrations', 'Healthcare Sales'],
    industry: 'Sales',
  },
  {
    title: 'Sales Operations Analyst',
    description: 'Support sales team with data analysis and process optimization.',
    company: 'Sales Analytics Co',
    location: 'Remote',
    remote: true,
    salary_min: 60000,
    salary_max: 80000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Sales Operations', 'Data Analysis', 'Excel', 'Salesforce', 'Reporting'],
    industry: 'Sales',
  },

  // Construction (10 jobs)
  {
    title: 'Project Manager - Construction',
    description: 'Oversee construction projects from planning to completion. Manage budgets and timelines.',
    company: 'BuildRight Construction',
    location: 'Phoenix, AZ',
    remote: false,
    salary_min: 85000,
    salary_max: 115000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Project Management', 'Construction', 'Budgeting', 'Scheduling', 'Team Leadership'],
    industry: 'Construction',
  },
  {
    title: 'Electrician',
    description: 'Install and maintain electrical systems for residential and commercial properties.',
    company: 'Sparks Electric',
    location: 'Houston, TX',
    remote: false,
    salary_min: 55000,
    salary_max: 75000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Electrical Work', 'NEC Code', 'Troubleshooting', 'Safety', 'Blueprint Reading'],
    industry: 'Construction',
  },
  {
    title: 'Carpenter',
    description: 'Build and install structures and fixtures using wood and other materials.',
    company: 'Woodworks Construction',
    location: 'Portland, OR',
    remote: false,
    salary_min: 48000,
    salary_max: 65000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Carpentry', 'Blueprint Reading', 'Power Tools', 'Measurement', 'Safety'],
    industry: 'Construction',
  },
  {
    title: 'Civil Engineer',
    description: 'Design and oversee infrastructure projects including roads and buildings.',
    company: 'Engineering Design Group',
    location: 'Charlotte, NC',
    remote: false,
    salary_min: 75000,
    salary_max: 105000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Civil Engineering', 'AutoCAD', 'Project Design', 'Site Planning', 'Regulations'],
    industry: 'Construction',
  },
  {
    title: 'Construction Estimator',
    description: 'Calculate project costs and prepare bids for construction projects.',
    company: 'Construction Solutions',
    location: 'Dallas, TX',
    remote: false,
    salary_min: 65000,
    salary_max: 90000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Cost Estimation', 'Takeoffs', 'Bidding', 'Construction Knowledge', 'Excel'],
    industry: 'Construction',
  },
  {
    title: 'Site Superintendent',
    description: 'Supervise construction site operations and ensure work quality and safety.',
    company: 'Premier Builders',
    location: 'Atlanta, GA',
    remote: false,
    salary_min: 75000,
    salary_max: 100000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Construction Management', 'Safety', 'Quality Control', 'Team Leadership', 'Scheduling'],
    industry: 'Construction',
  },
  {
    title: 'HVAC Technician',
    description: 'Install and repair heating, ventilation, and air conditioning systems.',
    company: 'Climate Control Services',
    location: 'Las Vegas, NV',
    remote: false,
    salary_min: 50000,
    salary_max: 68000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['HVAC', 'Troubleshooting', 'Installation', 'Maintenance', 'Customer Service'],
    industry: 'Construction',
  },
  {
    title: 'Plumber',
    description: 'Install and repair plumbing systems in residential and commercial buildings.',
    company: 'FlowRight Plumbing',
    location: 'Denver, CO',
    remote: false,
    salary_min: 52000,
    salary_max: 72000,
    experience_level: 'mid',
    job_type: 'full_time',
    skills: ['Plumbing', 'Pipe Installation', 'Troubleshooting', 'Building Codes', 'Safety'],
    industry: 'Construction',
  },
  {
    title: 'Construction Laborer',
    description: 'Assist with various construction tasks including site preparation and material handling.',
    company: 'General Construction Co',
    location: 'Tampa, FL',
    remote: false,
    salary_min: 35000,
    salary_max: 48000,
    experience_level: 'entry',
    job_type: 'full_time',
    skills: ['Physical Labor', 'Safety', 'Teamwork', 'Equipment Operation', 'Following Instructions'],
    industry: 'Construction',
  },
  {
    title: 'Construction Safety Manager',
    description: 'Develop and implement safety programs for construction sites.',
    company: 'Safety First Construction',
    location: 'Chicago, IL',
    remote: false,
    salary_min: 80000,
    salary_max: 110000,
    experience_level: 'senior',
    job_type: 'full_time',
    skills: ['Safety Management', 'OSHA', 'Risk Assessment', 'Training', 'Compliance'],
    industry: 'Construction',
  },
];

export async function seed100Jobs(employerId: string): Promise<{
  success: boolean;
  inserted: number;
  errors: number;
  message: string;
}> {
  console.log(`[Seed100Jobs] Starting to seed ${jobs.length} jobs for employer ${employerId}...`);

  let successCount = 0;
  let errorCount = 0;

  for (const job of jobs) {
    try {
      const { error: insertError } = await supabase
        .from('jobs')
        .insert({
          employer_id: employerId,
          title: job.title,
          description: job.description,
          company: job.company,
          location: job.location,
          remote: job.remote,
          salary_min: job.salary_min,
          salary_max: job.salary_max,
          experience_level: job.experience_level,
          job_type: job.job_type,
          skills: job.skills,
          status: 'active',
        });

      if (insertError) {
        if (insertError.code === '23505') {
          console.log(`[Seed100Jobs] Job "${job.title}" already exists, skipping...`);
        } else {
          console.error(`[Seed100Jobs] Error inserting "${job.title}":`, insertError);
          errorCount++;
        }
      } else {
        successCount++;
        if (successCount % 10 === 0) {
          console.log(`[Seed100Jobs] Progress: ${successCount}/${jobs.length} jobs inserted`);
        }
      }
    } catch (error) {
      console.error(`[Seed100Jobs] Exception inserting "${job.title}":`, error);
      errorCount++;
    }
  }

  console.log(`[Seed100Jobs] Seeding complete: ${successCount} jobs inserted, ${errorCount} errors`);

  return {
    success: true,
    inserted: successCount,
    errors: errorCount,
    message: `Successfully seeded ${successCount} jobs across ${[...new Set(jobs.map(j => j.industry))].length} industries`,
  };
}

export async function getEmployerForSeeding(): Promise<string | null> {
  console.log('[Seed100Jobs] Finding employer for job seeding...');

  const { data: employers, error } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .eq('role', 'employer')
    .limit(1);

  if (error || !employers || employers.length === 0) {
    console.error('[Seed100Jobs] No employer found:', error);
    return null;
  }

  console.log(`[Seed100Jobs] Found employer: ${employers[0].full_name} (${employers[0].email})`);
  return employers[0].id;
}
